import { getApiClient } from "./apiClient";
import { setAuthTokens } from "./authStorage";
import {
  assignEntitySchema,
  overrideAssignmentSchema,
  type AssigneeType,
  type OverrideAssignmentPayload,
} from "@io-spoke/contracts/assignments";
import type { ApprovalChainProgress } from "@io-spoke/contracts/approvals";
import {
  confirmSignupSchema,
  forgotPasswordSchema,
  loginPayloadSchema,
  mfaChallengeSchema,
  newPasswordChallengeSchema,
  recoveryCodeSchema,
  registerPayloadSchema,
  resendSignupSchema,
  resetPasswordSchema,
  totpConfirmSchema,
  totpSetupResponseSchema,
  authenticatorStatusSchema,
} from "@io-spoke/contracts/auth";
import {
  requestMagicLinkSchema,
  verifyMagicLinkSchema,
  magicLinkVerificationResponseSchema,
} from "@io-spoke/contracts/magicLinks";
import {
  createThreadSchema,
  threadSnapshotResponseSchema,
  threadContextSnapshotResponseSchema,
} from "@io-spoke/contracts/threads";
import type { AiContextSnapshot } from "@io-spoke/contracts/threads";
export type { AiContextSnapshot } from "@io-spoke/contracts/threads";
import {
  bulkReviewSchema,
  createEntitySchema,
  updateEntitySchema,
  entityRoutingRationaleResponseSchema,
  type EntityRoutingRationaleResponse,
} from "@io-spoke/contracts/entities";
import {
  createSlaRuleSchema,
  updateSlaRuleSchema,
} from "@io-spoke/contracts/slaRules";
import {
  createTriageRuleSchema,
  updateTriageRuleSchema,
} from "@io-spoke/contracts/triageRules";
import { notificationPreferencesSchema } from "@io-spoke/contracts/notifications";
import { userPreferencesSchema } from "@io-spoke/contracts/userPreferences";
import {
  applyWorkflowTemplateSchema,
  updateWorkflowSchema,
} from "@io-spoke/contracts/workflows";

type LoginPayload = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export type MfaChallenge = {
  type: "totp" | "sms" | "new_password";
  session: string;
  email: string;
};

type RegisterPayload = {
  email: string;
  password: string;
  name?: string;
};

type LoginResponse = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  challenge?: MfaChallenge;
  user?: {
    id?: string;
    email?: string;
    name?: string;
  };
};

type RegisterResponse = {
  user: {
    id?: string;
    email?: string;
    name?: string;
  };
};

type ConfirmSignupPayload = {
  email: string;
  code: string;
};

type ResendSignupPayload = {
  email: string;
};

type MagicLinkRequestPayload = {
  email: string;
};

type MagicLinkVerifyPayload = {
  token: string;
};

export async function login(payload: LoginPayload) {
  const parsed = loginPayloadSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid login payload.");
  }
  const api = getApiClient();
  const response = await api.post<LoginResponse>(
    "/auth/login",
    {
      email: parsed.data.email,
      password: parsed.data.password,
    },
    { skipAuthRefresh: true } as any
  );
  const { accessToken, refreshToken, idToken, user, challenge } = response.data;

  if (challenge) {
    return response.data;
  }
  if (!accessToken) {
    throw new Error("Auth API did not return an access token.");
  }

  setAuthTokens(
    {
      accessToken,
      refreshToken,
      idToken,
      user,
    },
    parsed.data.rememberMe ?? false
  );

  return response.data;
}

export async function verifyMfaChallenge(payload: {
  email: string;
  code: string;
  session: string;
  rememberMe?: boolean;
}) {
  const parsed = mfaChallengeSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid MFA payload.");
  }
  const api = getApiClient();
  const response = await api.post<LoginResponse>(
    "/auth/mfa/verify",
    parsed.data,
    { skipAuthRefresh: true } as any
  );
  const { accessToken, refreshToken, idToken, user } = response.data;
  if (!accessToken) {
    throw new Error("Auth API did not return an access token.");
  }
  setAuthTokens(
    { accessToken, refreshToken, idToken, user },
    parsed.data.rememberMe ?? false
  );
  return response.data;
}

export async function completeNewPasswordChallenge(payload: {
  email: string;
  newPassword: string;
  session: string;
  rememberMe?: boolean;
}) {
  const parsed = newPasswordChallengeSchema.safeParse(payload);
  if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid new password.");
  const api = getApiClient();
  const response = await api.post<LoginResponse>("/auth/challenge/new-password", parsed.data, { skipAuthRefresh: true } as any);
  const { accessToken, refreshToken, idToken, user } = response.data;
  if (!accessToken) throw new Error("Auth API did not return an access token.");
  setAuthTokens({ accessToken, refreshToken, idToken, user }, parsed.data.rememberMe ?? false);
  return response.data;
}

export async function verifyRecoveryCode(payload: {
  email: string;
  code: string;
  rememberMe?: boolean;
}) {
  const parsed = recoveryCodeSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid recovery payload.");
  }
  const api = getApiClient();
  const response = await api.post<LoginResponse>(
    "/auth/mfa/recovery",
    parsed.data,
    { skipAuthRefresh: true } as any
  );
  const { accessToken, refreshToken, idToken, user } = response.data;
  if (!accessToken) {
    throw new Error("Auth API did not return an access token.");
  }
  setAuthTokens(
    { accessToken, refreshToken, idToken, user },
    parsed.data.rememberMe ?? false
  );
  return response.data;
}

export async function register(payload: RegisterPayload) {
  const parsed = registerPayloadSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid registration payload.");
  }
  const api = getApiClient();
  const response = await api.post<RegisterResponse>(
    "/auth/register",
    parsed.data,
    { skipAuthRefresh: true } as any
  );
  return response.data;
}

export async function confirmSignup(payload: ConfirmSignupPayload) {
  const parsed = confirmSignupSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid confirmation payload.");
  }
  const api = getApiClient();
  await api.post("/auth/confirm", parsed.data, { skipAuthRefresh: true } as any);
}

export async function resendSignupCode(payload: ResendSignupPayload) {
  const parsed = resendSignupSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid resend payload.");
  }
  const api = getApiClient();
  await api.post("/auth/resend", parsed.data, { skipAuthRefresh: true } as any);
}

export async function requestPasswordReset(payload: { email: string }) {
  const parsed = forgotPasswordSchema.safeParse(payload);
  if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid password reset request.");
  const api = getApiClient();
  await api.post("/auth/password/forgot", parsed.data, { skipAuthRefresh: true } as any);
}

export async function resetPassword(payload: { email: string; code: string; newPassword: string }) {
  const parsed = resetPasswordSchema.safeParse(payload);
  if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid password reset payload.");
  const api = getApiClient();
  await api.post("/auth/password/reset", parsed.data, { skipAuthRefresh: true } as any);
}

export async function requestMagicLink(payload: MagicLinkRequestPayload) {
  const parsed = requestMagicLinkSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid magic link payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ message: string; link?: string }>(
    "/auth/magic/request",
    parsed.data,
    { skipAuthRefresh: true } as any
  );
  return response.data;
}

export async function verifyMagicLink(payload: MagicLinkVerifyPayload) {
  const parsed = verifyMagicLinkSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid magic link payload.");
  }
  const api = getApiClient();
  const response = await api.post(
    "/auth/magic/verify",
    parsed.data,
    { skipAuthRefresh: true } as any
  );
  const result = magicLinkVerificationResponseSchema.parse(response.data);
  return result;
}

export async function getMfaStatus() {
  const api = getApiClient();
  const response = await api.get("/auth/2fa/status");
  return authenticatorStatusSchema.parse(response.data);
}

export async function startTotpSetup() {
  const api = getApiClient();
  const response = await api.post("/auth/2fa/setup", {});
  return totpSetupResponseSchema.parse(response.data);
}

export async function confirmTotpSetup(payload: { code: string; setupId: string }) {
  const parsed = totpConfirmSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid TOTP payload.");
  }
  const api = getApiClient();
  await api.post("/auth/2fa/confirm", parsed.data);
}

export async function disableTotp() {
  const api = getApiClient();
  await api.post("/auth/2fa/disable", {});
}

export async function regenerateRecoveryCodes() {
  const api = getApiClient();
  const response = await api.post<{ codes: string[] }>("/auth/2fa/recovery/regenerate");
  return response.data.codes;
}

export type InboxThread = {
  id: string;
  subject: string;
  messageCount: number;
  updatedAt: string;
  status: "new" | "triage" | "in_progress" | "resolved";
  priority: "low" | "medium" | "high" | "critical";
  summary: string;
  customer?: CustomerSummary;
  triageRule?: {
    id: string;
    name: string;
  };
  triageOwner?: {
    type: "team" | "role" | "agent" | "human";
    id: string;
  };
  intent?: string;
  intentConfidence?: number;
  slaState?: AiContextSnapshot["slaState"];
};

export type CustomerSummary = {
  id: string;
  name?: string;
  email?: string;
};

export type InboxFilters = {
  query?: string;
  status?: InboxThread["status"];
  priority?: InboxThread["priority"];
  from?: string;
  to?: string;
};

export async function getInboxThreads(filters: InboxFilters = {}) {
  const api = getApiClient();
  const params = new URLSearchParams();
  if (filters.query) {
    params.set("q", filters.query);
  }
  if (filters.status) {
    params.set("status", filters.status);
  }
  if (filters.priority) {
    params.set("priority", filters.priority);
  }
  if (filters.from) {
    params.set("from", filters.from);
  }
  if (filters.to) {
    params.set("to", filters.to);
  }
  const query = params.toString();
  const response = await api.get<{ threads: InboxThread[] }>(
    query ? `/threads?${query}` : "/threads"
  );
  return response.data.threads;
}

type IngestPayload = {
  subject?: string;
  messages: Array<{ author: string; body: string }>;
  customer?: {
    name?: string;
    email?: string;
  };
};

export async function ingestThread(payload: IngestPayload) {
  const parsed = createThreadSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid thread payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ thread: { id: string } }>(
    "/threads",
    parsed.data
  );
  return response.data.thread;
}

export type ThreadDetail = {
  id: string;
  subject?: string;
  summary: string;
  actionItems: string[];
  status: InboxThread["status"];
  priority: InboxThread["priority"];
  customer?: CustomerSummary;
  triageRule?: InboxThread["triageRule"];
  triageOwner?: InboxThread["triageOwner"];
  messages: Array<{ author: string; body: string }>;
  timeline: Array<{ id: string; type: string; summary: string; createdAt: string }>;
  createdAt: string;
  updatedAt: string;
  aiContextSnapshot?: AiContextSnapshot | null;
};

export async function getThreadDetail(threadId: string) {
  const api = getApiClient();
  const response = await api.get<{ thread: ThreadDetail }>(`/threads/${threadId}`);
  return response.data.thread;
}

export async function fetchThreadSnapshot(threadId: string) {
  const api = getApiClient();
  const response = await api.get<{ snapshot: string }>(
    `/threads/${threadId}/snapshot-export`
  );
  const parsed = threadSnapshotResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid snapshot response.");
  }
  return parsed.data.snapshot;
}

export async function getThreadContextSnapshot(threadId: string) {
  const api = getApiClient();
  const response = await api.get<{ snapshot: AiContextSnapshot | null }>(
    `/threads/${threadId}/context-snapshot`
  );
  const parsed = threadContextSnapshotResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid context snapshot response.");
  }
  return parsed.data.snapshot;
}

export async function refreshThreadContextSnapshot(threadId: string) {
  const api = getApiClient();
  const response = await api.post<{ snapshot: AiContextSnapshot }>(
    `/threads/${threadId}/context-snapshot`
  );
  const parsed = threadContextSnapshotResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid context snapshot response.");
  }
  if (!parsed.data.snapshot) {
    throw new Error("Context snapshot refresh returned no payload.");
  }
  return parsed.data.snapshot;
}

export type EntitySummary = {
  id: string;
  threadId?: string;
  type: string;
  title: string;
  description: string;
  systems: string[];
  customerImpact?: string;
  suggestedOwner?: {
    type: AssigneeType;
    id: string;
  } | null;
  confidence?: number;
  priority: string;
  status: string;
  updatedAt: string;
  slaRule?: {
    id: string;
    name: string;
  };
  slaTargetAt?: string;
  slaBreachedAt?: string;
  customerTier?: string;
  approvalChainTemplateId?: string;
  approvalChainProgress?: ApprovalChainProgress;
  assignment?: {
    assigneeType: AssigneeType;
    assigneeId: string;
    rationale?: string;
    isFallback?: boolean;
  } | null;
};

export type BulkReviewResult = {
  id: string;
  status: "success" | "error";
  error?: string;
  entity?: EntitySummary;
};

export async function proposeWorkItem(threadId: string) {
  const api = getApiClient();
  const response = await api.post<{ entity: EntitySummary }>(
    `/threads/${threadId}/propose`
  );
  return response.data.entity;
}

export async function createEntity(payload: {
  type: "ticket" | "task" | "incident" | "request";
  title: string;
  description: string;
  priority: "low" | "medium" | "high" | "critical";
  status?: "new" | "proposed" | "approved" | "in_progress" | "done";
  threadId?: string;
}) {
  const parsed = createEntitySchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid entity payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ entity: EntitySummary }>(
    "/entities",
    parsed.data
  );
  return response.data.entity;
}

export async function getReviewQueue(filters?: { sla?: "breached" }) {
  const api = getApiClient();
  const params = new URLSearchParams({ status: "proposed" });
  if (filters?.sla === "breached") {
    params.set("sla", "breached");
  }
  const response = await api.get<{ entities: EntitySummary[] }>(
    `/entities?${params.toString()}`
  );
  return response.data.entities;
}

export async function getEntities() {
  const api = getApiClient();
  const response = await api.get<{ entities: EntitySummary[] }>("/entities");
  return response.data.entities;
}

export async function updateEntity(entityId: string, updates: {
  title?: string;
  description?: string;
  systems?: string[];
  customerImpact?: string;
  confidence?: number;
  priority?: string;
}) {
  const parsed = updateEntitySchema.safeParse(updates);
  if (!parsed.success) {
    throw new Error(
      parsed.error.issues[0]?.message ?? "Invalid entity update payload."
    );
  }
  const api = getApiClient();
  const response = await api.patch<{ entity: EntitySummary }>(
    `/entities/${entityId}`,
    parsed.data
  );
  return response.data.entity;
}

export type SlaRule = {
  id: string;
  name: string;
  enabled: boolean;
  keywords: string[];
  minAgeHours?: number;
  priority?: string;
  slaHours?: number;
  createdAt: string;
  updatedAt: string;
};

export async function getSlaRules() {
  const api = getApiClient();
  const response = await api.get<{ rules: SlaRule[] }>("/sla-rules");
  return response.data.rules;
}

export async function createSlaRule(payload: {
  name: string;
  enabled?: boolean;
  keywords?: string[];
  minAgeHours?: number;
  priority?: string;
  slaHours?: number;
}) {
  const parsed = createSlaRuleSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid SLA rule payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ rule: SlaRule }>(
    "/sla-rules",
    parsed.data
  );
  return response.data.rule;
}

export async function updateSlaRule(
  ruleId: string,
  payload: {
    name?: string;
    enabled?: boolean;
    keywords?: string[];
    minAgeHours?: number | null;
    priority?: string | null;
    slaHours?: number | null;
  }
) {
  const parsed = updateSlaRuleSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid SLA rule payload.");
  }
  const api = getApiClient();
  const response = await api.patch<{ rule: SlaRule }>(
    `/sla-rules/${ruleId}`,
    parsed.data
  );
  return response.data.rule;
}

export async function approveEntity(entityId: string) {
  const api = getApiClient();
  const response = await api.post<{ entity: EntitySummary }>(
    `/entities/${entityId}/approve`
  );
  return response.data.entity;
}

export async function bulkReviewEntities(action: "approve" | "reject", ids: string[]) {
  const parsed = bulkReviewSchema.safeParse({ action, ids });
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid bulk review payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ action: string; results: BulkReviewResult[] }>(
    "/entities/bulk-review",
    parsed.data
  );
  return response.data.results;
}

export async function assignEntity(
  entityId: string,
  payload: { assigneeType: AssigneeType; assigneeId: string; rationale?: string }
) {
  const parsed = assignEntitySchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid assignment payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ assignment: { id: string } }>(
    `/entities/${entityId}/assign`,
    parsed.data
  );
  return response.data.assignment;
}

export async function overrideAssignment(
  entityId: string,
  payload: OverrideAssignmentPayload
) {
  const parsed = overrideAssignmentSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid override payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ assignment: { id: string } }>(
    `/entities/${entityId}/assign/override`,
    parsed.data
  );
  return response.data.assignment;
}

export type EntityDetail = {
  entity: EntitySummary & {
    systems: string[];
    customerImpact?: string;
    suggestedOwner?: { type: AssigneeType; id: string } | null;
    customer?: CustomerSummary;
  };
  assignment: {
    assigneeType: AssigneeType;
    assigneeId: string;
    rationale?: string;
    createdAt: string;
  } | null;
  timeline: Array<{
    id: string;
    type: string;
    summary: string;
    actorType: string;
    actorId: string;
    rationale?: string;
    contextExcerpt?: string;
    createdAt: string;
  }>;
};

export type WorkflowStep = {
  id: string;
  entityId: string;
  position: number;
  label: string;
  status: "pending" | "in_progress" | "done";
  createdAt: string;
  updatedAt: string;
};

export type WorkflowTemplateSummary = {
  name: string;
  steps: string[];
};

export type SummaryPayload = {
  windowDays: number;
  team: string | null;
  generatedAt: string;
  nextRefreshAt: string;
  counts: {
    total: number;
    open: number;
    blockers: number;
  };
  kpis: {
    slaComplianceRate: number | null;
    averageTimeToApprovalHours: number | null;
    aiAssistRate: number | null;
  };
  trend: {
    direction: "up" | "down" | "flat";
    delta: number;
    percent: number | null;
    previousTotal: number;
  };
  sentiment: "stable" | "mixed" | "risk";
  blockers: Array<{
    id: string;
    title: string;
    priority: string;
    status: string;
    link: string;
  }>;
  ai: {
    inferenceCount: number;
    assistRate: number | null;
    fallbackRate: number | null;
    cacheHitRate: number | null;
    averageConfidence: number | null;
  };
  series: Array<{
    date: string;
    total: number;
    open: number;
    blockers: number;
    slaEligible: number;
    slaCompliant: number;
    aiAssisted: number;
  }>;
  summary: string;
};

export type Notification = {
  id: string;
  recipientType: "user" | "team" | "role";
  recipientId: string;
  type: string;
  title: string;
  body: string;
  entityId?: string;
  readAt?: string;
  createdAt: string;
};

export type CustomerProfile = {
  customer: CustomerSummary;
  recentThreads: Array<{
    id: string;
    subject?: string;
    summary: string;
    updatedAt: string;
    status: string;
    priority: string;
  }>;
  openWorkItems: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    updatedAt: string;
  }>;
  summaries: Array<{
    threadId: string;
    summary: string;
    updatedAt: string;
  }>;
};

export async function getCustomerProfile(customerId: string) {
  const api = getApiClient();
  const response = await api.get<{ profile: CustomerProfile }>(
    `/customers/${customerId}`
  );
  return response.data.profile;
}

export type TriageRule = {
  id: string;
  name: string;
  enabled: boolean;
  keywords: string[];
  priority?: string;
  status?: string;
  triageOwner?: {
    type: "team" | "role" | "agent" | "human";
    id: string;
  };
  createdAt: string;
  updatedAt: string;
};

export async function getTriageRules() {
  const api = getApiClient();
  const response = await api.get<{ rules: TriageRule[] }>("/triage-rules");
  return response.data.rules;
}

export async function createTriageRule(payload: {
  name: string;
  enabled?: boolean;
  keywords?: string[];
  priority?: string;
  status?: string;
  triageOwner?: { type: "team" | "role" | "agent" | "human"; id: string };
}) {
  const parsed = createTriageRuleSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid triage rule payload.");
  }
  const api = getApiClient();
  const response = await api.post<{ rule: TriageRule }>(
    "/triage-rules",
    parsed.data
  );
  return response.data.rule;
}

export async function updateTriageRule(
  ruleId: string,
  payload: {
    name?: string;
    enabled?: boolean;
    keywords?: string[];
    priority?: string;
    status?: string;
    triageOwner?: { type: "team" | "role" | "agent" | "human"; id: string } | null;
  }
) {
  const parsed = updateTriageRuleSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid triage rule payload.");
  }
  const api = getApiClient();
  const response = await api.patch<{ rule: TriageRule }>(
    `/triage-rules/${ruleId}`,
    parsed.data
  );
  return response.data.rule;
}

export type NotificationPreferences = {
  userId: string;
  teamId?: string;
  roleId?: string;
  inAppEnabled: boolean;
  digestEnabled: boolean;
  digestHourUtc: number;
};

export type UserPreferences = {
  userId: string;
  theme: "light" | "dark";
  density: "comfortable" | "compact";
  lastActiveOrgId?: string;
};

export async function getNotifications(options?: {
  status?: "all" | "unread";
  limit?: number;
}) {
  const api = getApiClient();
  const params = new URLSearchParams();
  if (options?.status) {
    params.set("status", options.status);
  }
  if (options?.limit) {
    params.set("limit", String(options.limit));
  }
  const query = params.toString();
  const response = await api.get<{ notifications: Notification[] }>(
    query ? `/notifications?${query}` : "/notifications"
  );
  return response.data.notifications;
}

export async function markNotificationRead(notificationId: string) {
  const api = getApiClient();
  const response = await api.post<{ ok: boolean }>(
    `/notifications/${notificationId}/read`
  );
  return response.data.ok;
}

export async function markAllNotificationsRead() {
  const api = getApiClient();
  const response = await api.post<{ ok: boolean }>(
    "/notifications/read-all"
  );
  return response.data.ok;
}

export async function getNotificationPreferences() {
  const api = getApiClient();
  const response = await api.get<{ preferences: NotificationPreferences }>(
    "/notifications/preferences"
  );
  return response.data.preferences;
}

export type EntityAuditExportOptions = {
  from?: string;
  to?: string;
  format?: "csv" | "markdown";
};

export async function exportEntityAuditLog(
  entityId: string,
  options?: EntityAuditExportOptions
) {
  const api = getApiClient();
  const params: Record<string, string> = {};
  if (options?.from) {
    params.from = options.from;
  }
  if (options?.to) {
    params.to = options.to;
  }
  if (options?.format) {
    params.format = options.format;
  }
  const response = await api.get<Blob>(`/entities/${entityId}/audit-export`, {
    params: Object.keys(params).length ? params : undefined,
    responseType: "blob",
  });
  return response.data;
}

export async function updateNotificationPreferences(
  updates: Partial<Omit<NotificationPreferences, "userId">>
) {
  const parsed = notificationPreferencesSchema.safeParse(updates);
  if (!parsed.success) {
    throw new Error(
      parsed.error.issues[0]?.message ?? "Invalid notification preferences payload."
    );
  }
  const api = getApiClient();
  const response = await api.patch<{ preferences: NotificationPreferences }>(
    "/notifications/preferences",
    parsed.data
  );
  return response.data.preferences;
}

export async function getUserPreferences() {
  const api = getApiClient();
  const response = await api.get<{ preferences: UserPreferences }>(
    "/user-preferences"
  );
  return response.data.preferences;
}

export async function updateUserPreferences(
  updates: Partial<Omit<UserPreferences, "userId">>
) {
  const parsed = userPreferencesSchema.safeParse(updates);
  if (!parsed.success) {
    throw new Error(
      parsed.error.issues[0]?.message ?? "Invalid user preferences payload."
    );
  }
  const api = getApiClient();
  const response = await api.patch<{ preferences: UserPreferences }>(
    "/user-preferences",
    parsed.data
  );
  return response.data.preferences;
}

export async function sendDailyDigest() {
  const api = getApiClient();
  const response = await api.post<{ ok: boolean; sentTo: string }>(
    "/notifications/digest/send"
  );
  return response.data;
}

export async function getEntityDetail(entityId: string) {
  const api = getApiClient();
  const response = await api.get<EntityDetail>(`/entities/${entityId}`);
  return response.data;
}

export async function getEntityRoutingRationale(entityId: string) {
  const api = getApiClient();
  const response = await api.get<EntityRoutingRationaleResponse>(
    `/entities/${entityId}/routing-rationale`
  );
  const parsed = entityRoutingRationaleResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(
      parsed.error.issues[0]?.message ?? "Invalid routing rationale response."
    );
  }
  return parsed.data;
}

export async function getWorkflow(entityId: string) {
  const api = getApiClient();
  const response = await api.get<{
    steps: WorkflowStep[];
    template: string;
    templates: WorkflowTemplateSummary[];
  }>(`/entities/${entityId}/workflow`);
  return response.data;
}

export async function updateWorkflowStep(
  entityId: string,
  stepId: string,
  updates: { label?: string; status?: WorkflowStep["status"] }
) {
  const parsed = updateWorkflowSchema.safeParse(updates);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid workflow payload.");
  }
  const api = getApiClient();
  const response = await api.patch<{ step: WorkflowStep }>(
    `/entities/${entityId}/workflow/${stepId}`,
    parsed.data
  );
  return response.data.step;
}

export async function applyWorkflowTemplate(
  entityId: string,
  template: string
) {
  const parsed = applyWorkflowTemplateSchema.safeParse({ template });
  if (!parsed.success) {
    throw new Error(
      parsed.error.issues[0]?.message ?? "Invalid workflow template payload."
    );
  }
  const api = getApiClient();
  const response = await api.post<{
    steps: WorkflowStep[];
    template: string;
    templates: WorkflowTemplateSummary[];
  }>(`/entities/${entityId}/workflow/template`, parsed.data);
  return response.data;
}

export async function getSummary(params?: {
  range?: "7" | "30" | "90" | "all";
  windowDays?: number;
  team?: string;
}) {
  const api = getApiClient();
  const query: Record<string, string | number> = {};
  if (params?.range) {
    query.range = params.range;
  }
  if (typeof params?.windowDays === "number") {
    query.windowDays = params.windowDays;
  }
  if (params?.team) {
    query.team = params.team;
  }
  const response = await api.get<{ summary: SummaryPayload }>("/summary", {
    params: Object.keys(query).length > 0 ? query : undefined,
  });
  return response.data.summary;
}
